import React from "react";
import { Link, Route } from "react-router-dom";

import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import { AUTH_LEVELS } from "../lib/globals";
import { IUmpireUser } from "../types/api";

import AdminLayout from "../pages/Admin/AdminLayout";
import CurrentYear, {
    loader as CurrentYearLoader,
} from "../pages/Admin/CurrentYear";
import PastYear, { loader as PastYearLoader } from "../pages/Admin/PastYear";
import EjectionReports, {
    loader as EjectionReportsLoader,
} from "../pages/Admin/EjectionReports";
import Report, {
    loader as EjectionReportLoader,
} from "../pages/Admin/EjectionReports/components/Report";
import Umpires, {
    loader as UmpiresTypeLoader,
} from "../pages/Admin/Umpires/components/Umpires";
import Umpire, {
    loader as UmpireLoader,
} from "../pages/Admin/Umpires/components/Umpire";
import Ratings, {
    loader as RatingsLoader,
} from "../pages/Admin/Umpires/components/Ratings";
import UmpiresLayout, {
    loader as UmpiresLoader,
} from "../pages/Admin/Umpires/components/UmpiresLayout";
import BannedList, {
    loader as BannedListLoader,
} from "../pages/Admin/Umpires/components/BanList";
import SiteSettings, {
    loader as SiteSettingsLoader,
} from "../pages/Admin/SiteSettings";
import Bonus, {
    loader as BonusLoader,
} from "../pages/Admin/Umpires/components/Bonus";
import GameLogs, { loader as GameLogsLoader } from "../pages/Admin/GameLogs";

export default (
    <Route
        element={<RequireAuth allowedUserLevels={AUTH_LEVELS.sitemanagement} />}
    >
        <Route
            path="admin"
            handle={{ crumb: () => "Admin View" }}
            element={<AdminLayout />}
        >
            <Route
                path="ejection-reports"
                element={<EjectionReports />}
                loader={EjectionReportsLoader}
                handle={{
                    crumb: () => (
                        <Link to="/admin/ejection-reports">
                            Ejection Reports
                        </Link>
                    ),
                    header: () => "Ejection Reports",
                }}
            >
                <Route
                    path=":gameId"
                    element={<Report />}
                    loader={EjectionReportLoader}
                    handle={{
                        crumb: () => "Game Ejection Report",
                        header: () => "Game Ejection Report",
                    }}
                />
            </Route>
            <Route path="current-year" handle={{ crumb: () => "Current Year" }}>
                <Route
                    path="majorybl"
                    element={<CurrentYear />}
                    loader={params =>
                        CurrentYearLoader({
                            ...params,
                            site: 1,
                        })
                    }
                    handle={{
                        crumb: () => "MAJORYBL",
                        header: () => "Manage MAJORYBL Umpires",
                    }}
                />
                <Route
                    path="wisconsin-baseball-tournaments"
                    element={<CurrentYear />}
                    loader={params =>
                        CurrentYearLoader({
                            ...params,
                            site: 2,
                        })
                    }
                    handle={{
                        crumb: () => "Wisconsin Baseball Tournaments",
                        header: () =>
                            "Manage Wisconsin Baseball Tournaments Umpires",
                    }}
                />
                <Route
                    path="illinois-baseball-tournaments"
                    element={<CurrentYear />}
                    loader={params =>
                        CurrentYearLoader({
                            ...params,
                            site: 3,
                        })
                    }
                    handle={{
                        crumb: () => "Illinois Baseball Tournaments",
                        header: () =>
                            "Manage Illinois Baseball Tournaments Umpires",
                    }}
                />
            </Route>
            <Route path="past-year" handle={{ crumb: () => "Current Year" }}>
                <Route
                    path="majorybl"
                    element={<PastYear />}
                    loader={params =>
                        PastYearLoader({
                            ...params,
                            site: 1,
                        })
                    }
                    handle={{
                        crumb: () => "MAJORYBL Archive",
                        header: () => "MAJORYBL Umpires Archive",
                    }}
                />
                <Route
                    path="wisconsin-baseball-tournaments"
                    element={<PastYear />}
                    loader={params =>
                        PastYearLoader({
                            ...params,
                            site: 2,
                        })
                    }
                    handle={{
                        crumb: () => "Wisconsin Baseball Tournaments Archive",
                        header: () =>
                            "Wisconsin Baseball Tournaments Umpires Archive",
                    }}
                />
                <Route
                    path="illinois-baseball-tournaments"
                    element={<PastYear />}
                    loader={params =>
                        PastYearLoader({
                            ...params,
                            site: 3,
                        })
                    }
                    handle={{
                        crumb: () => "Illinois Baseball Tournaments Archive",
                        header: () =>
                            "Illinois Baseball Tournaments Umpires Archive",
                    }}
                />
            </Route>
            <Route
                path="umpires"
                handle={{
                    crumb: () => "Umpires",
                }}
                loader={UmpiresLoader}
                element={<UmpiresLayout />}
            >
                <Route
                    path="pending"
                    element={<Umpires />}
                    loader={params =>
                        UmpiresTypeLoader({
                            ...params,
                            type: "pending",
                        })
                    }
                    handle={{
                        crumb: () => "Pending Umpires",
                        header: () => "Pending Umpires",
                    }}
                />
                <Route
                    path="active"
                    element={<Umpires />}
                    loader={params =>
                        UmpiresTypeLoader({
                            ...params,
                            type: "active",
                        })
                    }
                    handle={{
                        crumb: () => "Active Umpires",
                        header: () => "Active Umpires",
                    }}
                />
                <Route
                    path="denied"
                    element={<Umpires />}
                    loader={params =>
                        UmpiresTypeLoader({
                            ...params,
                            type: "denied",
                        })
                    }
                    handle={{
                        crumb: () => "Denied Umpires",
                        header: () => "Denied Umpires",
                    }}
                />
                <Route
                    path="inactive"
                    element={<Umpires />}
                    loader={params =>
                        UmpiresTypeLoader({
                            ...params,
                            type: "inactive",
                        })
                    }
                    handle={{
                        crumb: () => "Inactive Umpires",
                        header: () => "Inactive Umpires",
                    }}
                />
                <Route
                    path=":umpireId"
                    element={<Umpire />}
                    loader={UmpireLoader}
                    handle={{
                        crumb: () => "Umpire Info",
                        header: ({ umpire }: { umpire: IUmpireUser }) =>
                            `Umpire Info: ${umpire.name}`,
                    }}
                />
                <Route
                    path="ratings"
                    element={<Ratings />}
                    loader={RatingsLoader}
                    handle={{
                        crumb: () => "Umpire Ratings",
                        header: () => "Umpire Ratings",
                    }}
                />
                <Route
                    path="ban-list"
                    element={<BannedList />}
                    loader={BannedListLoader}
                    handle={{
                        crumb: () => "Team Ban List",
                        header: () => "Team Ban List",
                    }}
                />
                <Route
                    path="bonus"
                    element={<Bonus />}
                    loader={BonusLoader}
                    handle={{
                        crumb: () => "Yearly Bonus Numbers",
                        header: () => "Yearly Bonus Numbers",
                    }}
                />
            </Route>
            <Route
                path="settings"
                element={<SiteSettings />}
                loader={SiteSettingsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/admin/settings`}>Site Settings</Link>
                        );
                    },
                    header: () => "Site Settings",
                }}
            />
            <Route
                path="game-logs"
                handle={{
                    crumb: () => "Game Logs",
                    header: () => "Game Logs",
                }}
            >
                <Route
                    path=":gameId"
                    element={<GameLogs />}
                    loader={GameLogsLoader}
                    handle={{
                        crumb: () => "Game Log",
                        header: () => "Game Log",
                    }}
                />
                <Route
                    path="placeholder"
                    element={<GameLogs />}
                    loader={GameLogsLoader}
                    handle={{
                        crumb: () => "Game Log",
                        header: () => "Game Log",
                    }}
                />
            </Route>
        </Route>
        <Route path="*" element={<NotFound parent="admin" />} />
    </Route>
);
